import { createCombinedEmailRunner } from './combinedEmailLoader';
import { isCompanyActionEligible } from "../../helper/companyEligibility";
import {
  executeAiEnrichmentApi,
  findEmailBulkApi,
  findSpreadsheetEmailBulkApi,
  verifyEmailBulkApi,
  verifySpreadsheetEmailBulkApi,
} from '../../api/enrichmentApi';
import {
  DEFAULT_SPREADSHEET_MODEL_ID,
  getSpreadsheetModelById,
} from '../../helper/aiEnrichment';
import {
  extractDomain,
  formatVerifyEmailCellError,
  normalizeEmails,
  parseApiError,
} from '../../helper/spreadsheetView';
import { classifySingleAiEnrichment } from './classifySingleAiEnrichment';

/**
 * Run find_email / verify_email / ai_enrichment for a single company.
 * Calls onStatus with { status, result?, error?, actionType, email?, score?, summary? }.
 */
export async function runSingleCompanyEnrichment({
  actionType,
  company,
  options = {},
  draftSessionId,
  onDraftSessionUsed,
  onStatus,
}) {
  const companyId = company?.stagedId;
  if (!isCompanyActionEligible(company)) {
    onStatus?.({ actionType, status: 'failed', error: 'Company not found' });
    return;
  }

  onStatus?.({ actionType, status: 'running' });

  if (actionType === 'find_and_verify_email') {
    try {
      const { combinedEmailStatus } = await import('./combinedEmailOperation');
      const run = options.combinedEmailRunner || createCombinedEmailRunner();
      const data = await run({ companyIds: [companyId], draftSessionId });
      if (draftSessionId) onDraftSessionUsed?.();
      const result = data.results.find((item) => item.company_id === companyId);
      onStatus?.({ actionType, ...combinedEmailStatus(result) });
    } catch (error) {
      const { msg } = parseApiError(error, 'Find and verify failed. Retry to recover the operation.');
      onStatus?.({ actionType, status: 'failed', error: msg });
    }
    return;
  }

  if (actionType === 'find_email') {
    const domain = extractDomain(company?.primaryWebsite || company?.domain);
    if (!draftSessionId && !domain) {
      onStatus?.({ actionType, status: 'failed', error: 'Not found' });
      return;
    }

    try {
      const resp = draftSessionId
        ? await findSpreadsheetEmailBulkApi({
          company_ids: [companyId],
          draft_session_id: draftSessionId,
        })
        : await findEmailBulkApi({ domain: [domain] });
      if (draftSessionId) onDraftSessionUsed?.();

      const results = resp?.data?.results || [];
      const match = results.find((r) => r.company_id === companyId)
        || results.find((r) => r.domain === domain)
        || results[0];

      if (match?.found && match.email) {
        onStatus?.({
          actionType,
          status: 'success',
          result: match.email,
          email: match.email,
          title: 'Email found',
        });
      } else {
        onStatus?.({ actionType, status: 'failed', error: 'Not found' });
      }
    } catch (error) {
      const { msg } = parseApiError(error, 'Find Email failed');
      onStatus?.({ actionType, status: 'failed', error: msg });
    }
    return;
  }

  if (actionType === 'verify_email') {
    const emails = normalizeEmails(company?.emails);
    if (!emails.length) {
      onStatus?.({ actionType, status: 'failed', error: 'Missing emails' });
      return;
    }

    try {
      const verifyItems = emails.map((email) => ({ company_id: companyId, email }));
      const resp = draftSessionId
        ? await verifySpreadsheetEmailBulkApi({
          items: verifyItems,
          provider: 'hunter',
          draft_session_id: draftSessionId,
        })
        : await verifyEmailBulkApi({ emails, provider: 'hunter' });
      if (draftSessionId) onDraftSessionUsed?.();

      const results = resp?.data?.results || [];
      let best = null;
      results.forEach((r) => {
        if (r.status !== 'success') return;
        if (!best || (r.score || 0) > (best.score || 0)) best = r;
      });

      if (best) {
        const score = best.score || 0;
        const deliverable = score >= 70 ? 'Deliverable' : `Score ${score}%`;
        onStatus?.({
          actionType,
          status: 'success',
          result: score,
          email: best.email,
          score,
          title: 'Email verified',
          detail: `${best.email} · ${deliverable}`,
        });
      } else {
        onStatus?.({ actionType, status: 'failed', error: 'No verifications succeeded' });
      }
    } catch (error) {
      const { msg } = parseApiError(error, 'Verify Email failed');
      onStatus?.({ actionType, status: 'failed', error: formatVerifyEmailCellError(msg) });
    }
    return;
  }

  if (actionType === 'ai_enrichment') {
    const query = String(options.query || '').trim();
    if (!query) {
      onStatus?.({ actionType, status: 'failed', error: 'Please enter an AI prompt' });
      return;
    }

    const modelIdStr = String(options.model || DEFAULT_SPREADSHEET_MODEL_ID).trim();
    const modelObj = getSpreadsheetModelById(modelIdStr);
    const llmProvider = modelObj.provider;

    try {
      const resp = await executeAiEnrichmentApi({
        company_id: companyId,
        query,
        llm_provider: llmProvider,
        model: modelIdStr,
        ...(draftSessionId ? { draft_session_id: draftSessionId } : {}),
      });
      if (draftSessionId) onDraftSessionUsed?.();
      const payload = resp?.data || {};
      onStatus?.({ actionType, ...classifySingleAiEnrichment(payload) });
    } catch (error) {
      const { msg } = parseApiError(error, 'AI enrichment failed');
      onStatus?.({ actionType, status: 'failed', error: msg });
    }
  }
}
